import firebase from 'firebase/compat/app';

class FranchiseAPI {
  constructor(db, fieldValue) {
    this.db = db;
    this.fieldValue = fieldValue;
  }

  // Franchise Owner Methods
  franchiseOwner = uid => this.db.doc(`franchiseOwners/${uid}`);
  franchiseOwners = () => this.db.collection('franchiseOwners');

  // Franchise Methods
  franchise = franchiseId => this.db.doc(`franchises/${franchiseId}`);
  franchises = () => this.db.collection('franchises');

  // Team Members Methods
  franchiseTeamMember = uid => this.db.doc(`franchiseTeamMembers/${uid}`);
  franchiseTeamMembers = franchiseId => 
    this.db.collection('franchiseTeamMembers').where('franchiseId', '==', franchiseId);

  // Onboarding Methods
  franchiseOnboarding = franchiseId => this.db.doc(`franchiseOnboarding/${franchiseId}`);
  
  // Leads Methods
  franchiseLeads = franchiseId => 
    this.db.collection('franchiseLeads').where('franchiseId', '==', franchiseId);

  // Branding Materials Methods
  franchiseBrandingMaterials = franchiseId => 
    this.db.collection('franchiseBrandingMaterials').where('franchiseId', '==', franchiseId);

  // Helper Functions
  
  // Create new franchise owner
  createFranchiseOwner = async (uid, data) => {
    const franchiseOwnerData = {
      personalEmail: data.email,
      franchiseEmail: data.franchiseEmail || null,
      franchiseId: data.franchiseId,
      displayName: data.displayName,
      phone: data.phone || null,
      status: 'pending',
      onboardingCompleted: false,
      createdAt: this.fieldValue.serverTimestamp(),
    };
    
    return this.franchiseOwner(uid).set(franchiseOwnerData);
  };

  // Create new franchise
  createFranchise = async (ownerId, paymentId = null) => {
    // Generate franchise ID
    const franchiseRef = this.franchises().doc();
    const franchiseId = franchiseRef.id;
    
    const franchiseData = {
      ownerId,
      businessName: null,
      territory: {
        city: null,
        areas: [],
        pincode: []
      },
      status: paymentId ? 'onboarding' : 'pending_payment',
      paymentId,
      createdAt: this.fieldValue.serverTimestamp(),
      activatedAt: null,
    };
    
    await franchiseRef.set(franchiseData);
    return franchiseId;
  };

  // Initialize onboarding
  initializeOnboarding = async (franchiseId) => {
    const onboardingData = {
      step: 'personal_details',
      personalDetails: null,
      teamMembers: [],
      territory: null,
      brandingRequests: null,
      preferences: null,
      submittedAt: null,
      approvedAt: null,
      approvedBy: null,
    };
    
    return this.franchiseOnboarding(franchiseId).set(onboardingData);
  };

  // Update onboarding step
  updateOnboardingStep = async (franchiseId, step, data, userId = null) => {
    const updates = {
      step,
      [step]: data,
    };
    
    // Add userId if provided (for first update)
    if (userId) {
      updates.userId = userId;
      updates.franchiseId = franchiseId;
    }
    
    return this.franchiseOnboarding(franchiseId).update(updates);
  };

  // Submit onboarding
  submitOnboarding = async (franchiseId, submissionData = {}, userId = null) => {
    const updates = {
      step: 'completed',
      submittedAt: this.fieldValue.serverTimestamp(),
      ...submissionData,
    };
    
    // Add userId if provided
    if (userId) {
      updates.userId = userId;
      updates.franchiseId = franchiseId;
    }

    return this.franchiseOnboarding(franchiseId).set(updates, { merge: true });
  };

  // Approve franchise
  approveFranchise = async (franchiseId, approvedBy) => {
    // Update franchise status
    await this.franchise(franchiseId).update({
      status: 'active',
      activatedAt: this.fieldValue.serverTimestamp(),
    });
    
    // Update onboarding
    return this.franchiseOnboarding(franchiseId).update({
      approvedAt: this.fieldValue.serverTimestamp(),
      approvedBy,
    });
  };

  // Add team member
  addTeamMember = async (franchiseId, memberData) => {
    const teamMemberRef = this.db.collection('franchiseTeamMembers').doc();
    
    const teamMemberData = {
      franchiseId,
      franchiseEmail: memberData.franchiseEmail || null,
      personalEmail: memberData.email,
      displayName: memberData.name,
      role: memberData.role,
      phone: memberData.phone || null,
      addedBy: memberData.addedBy,
      status: 'invited',
      createdAt: this.fieldValue.serverTimestamp(),
    };
    
    return teamMemberRef.set(teamMemberData);
  };

  // Get franchise by owner ID
  getFranchise = async (ownerId) => {
    try {
      const snapshot = await this.franchises()
        .where('ownerId', '==', ownerId)
        .limit(1)
        .get();
      
      if (!snapshot.empty) {
        const doc = snapshot.docs[0];
        return {
          id: doc.id,
          ...doc.data()
        };
      }
      return null;
    } catch (error) {
      console.error('Error getting franchise:', error);
      return null;
    }
  };

  // Check if user is franchise owner
  checkFranchiseOwnership = async (uid) => {
    const doc = await this.franchiseOwner(uid).get();
    if (doc.exists) {
      const data = doc.data();
      return {
        isFranchiseOwner: true,
        franchiseId: data.franchiseId,
        status: data.status,
        onboardingCompleted: data.onboardingCompleted,
      };
    }
    return { isFranchiseOwner: false };
  };

  // Check if user is team member
  checkTeamMembership = async (uid) => {
    const doc = await this.franchiseTeamMember(uid).get();
    if (doc.exists) {
      const data = doc.data();
      return {
        isTeamMember: true,
        franchiseId: data.franchiseId,
        role: data.role,
        status: data.status,
      };
    }
    return { isTeamMember: false };
  };
}

export default FranchiseAPI;

